import { Flex, Text } from '@chakra-ui/react';
import { faBan } from '@fortawesome/pro-light-svg-icons';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonText, OutlineCard, useToast } from '@morressier/alembic';
import { useNavigate } from 'react-router-dom';
import { useCoReviewInvitationDecision } from 'src/api/commands';
import { useKeycloak } from 'src/components/AuthHOC';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import SingleItem from 'src/layouts/SingleItem';
import { RichTextDisplay } from '../../components/Submission/GenericFieldComponents/RichText/RichTextDisplay';

export const CoReviewInvitation = () => {
  const {
    invalidateReviewContextData,
    review,
    submissionContext,
    otherCoReviewer,
    fieldsResponse
  } = useReviewData();
  const {
    caseData: { reviewer, coReview }
  } = review;
  const toast = useToast();
  const navigate = useNavigate();
  const { currentUser } = useKeycloak();

  const queryKey = ['review', { id: review.id }];
  const acceptCoReviewInvitationMutation = useCoReviewInvitationDecision({
    reviewId: review.id,
    decision: 'acceptRequest',
    queryKey
  });
  const declineCoReviewInvitationMutation = useCoReviewInvitationDecision({
    reviewId: review.id,
    decision: 'declineRequest',
    queryKey
  });

  const declineReview = () => {
    const { id, submissionContextId } = review;
    const submissionId = review.caseData.submission.id;
    navigate(
      `/reviews/${id}/decline-coreview?submissionId=${submissionId}&ctxId=${submissionContextId}`
    );
  };

  const acceptCoReviewInvitation = async () => {
    await acceptCoReviewInvitationMutation.mutateAsync();
    invalidateReviewContextData();
  };

  const declineCoReviewInvitation = async () => {
    await declineCoReviewInvitationMutation.mutateAsync();
    invalidateReviewContextData();
    declineReview();
  };

  const acceptCoReview = async () => {
    await acceptCoReviewInvitation();
    toast({
      variant: 'success',
      title: 'Co-reviewing with ' + otherCoReviewer?.firstName,
      description:
        'Both of you have full editing access, and will receive equal credits in case you opt-in.'
    });
  };

  const submissionTitle =
    (fieldsResponse?.fields?.Title?.value as string) ||
    review.caseData.submission.title;

  return (
    <SingleItem.Content>
      <OutlineCard borderless padding={8} width="100%">
        <Flex flexDirection="column" gap={4}>
          <Flex flexDirection="column">
            <Text textStyle="headingGroup">Invitation</Text>
            <Text>
              Dear {coReview?.coReviewer.firstName || currentUser.firstName},
            </Text>
            <Text>
              {review.caseData.reviewer.firstName} has invited you to co-review
              this submission in {submissionContext.data.name}:
              <span style={{ display: 'inline-flex' }}>
                "<RichTextDisplay value={submissionTitle} />"
              </span>
            </Text>
          </Flex>

          <Flex flexDirection="column">
            {coReview?.invitation?.note ? (
              <>
                <Text fontWeight="bold">Note from {reviewer.firstName}:</Text>
                <Text>{coReview?.invitation?.note}</Text>
              </>
            ) : null}
          </Flex>

          <Text>
            If you accept, you will colaborate with {reviewer.firstName} in
            real-time, with shared editing access and equal credit.
          </Text>

          <Flex justifyContent="flex-end" gap={2} marginTop={6}>
            <ButtonText
              size="context"
              variant="caution"
              onClick={declineCoReviewInvitation}
              disabled={
                acceptCoReviewInvitationMutation.isLoading ||
                declineCoReviewInvitationMutation.isLoading
              }
            >
              <FontAwesomeIcon icon={faBan} />
              Decline
            </ButtonText>

            <Button
              size="context"
              variant="primary"
              onClick={acceptCoReview}
              isLoading={acceptCoReviewInvitationMutation.isLoading}
              isDisabled={
                acceptCoReviewInvitationMutation.isLoading ||
                declineCoReviewInvitationMutation.isLoading
              }
            >
              <FontAwesomeIcon icon={faCheck} />
              Accept
            </Button>
          </Flex>
        </Flex>
      </OutlineCard>
    </SingleItem.Content>
  );
};
